// src/Components/CommentsSection.js
import React, { useState, useMemo, useEffect } from "react";
import IconButton from "../Images/IconButton";
import { fmtDateTime } from "../Utils/dates";
import useResponsive from "../hooks/useResponsive";
import { VIEW_BREAKPOINTS } from "../Config/responsive";
import styles from "../Styles/Announcementnew2.module.css";

/* --------------------------------------------------
   Yleisapuja kommenttien käsittelyyn
-------------------------------------------------- */
function toMs(v) {
  if (!v) return 0;
  if (typeof v === "number") return v < 2e12 ? v * 1000 : v;
  if (typeof v?.toMillis === "function") return v.toMillis();
  if (typeof v?._seconds === "number") return v._seconds * 1000;
  const t = Date.parse(v);
  return Number.isNaN(t) ? 0 : t;
}

function normalizeComment(m) {
  return {
    id: m.id,
    body: m.body || "",
    senderId: m.senderId || null,
    senderName: m.senderName || "",
    createdAt: m.createdAt || Date.now(),
    likes: Array.isArray(m.likedBy) ? m.likedBy.length : (m.likes || 0),
    likedBy: Array.isArray(m.likedBy)
      ? m.likedBy.map((lb) => lb.name || lb)
      : [],
    parentId: m.parentId ?? null,
    replies: Array.isArray(m.replies) ? m.replies.map(normalizeComment) : [],
  };
}

function normalizeComments(list) {
  return (list || []).map(normalizeComment);
}

function buildThreadFromList(list) {
  const items = Array.isArray(list) ? list : [];
  const byId = new Map();
  const roots = [];

  for (const m of items) {
    byId.set(m.id, { ...m, replies: [] });
  }

  for (const m of items) {
    const node = byId.get(m.id);
    const pid = m.parentId || null;
    if (pid && byId.has(pid)) {
      byId.get(pid).replies.push(node);
    } else {
      roots.push(node);
    }
  }

  const byCreatedDesc = (a, b) => toMs(b.createdAt) - toMs(a.createdAt);
  const byCreatedAsc = (a, b) => toMs(a.createdAt) - toMs(b.createdAt);
  roots.sort(byCreatedDesc);

  for (const r of roots) {
    if (Array.isArray(r.replies)) r.replies.sort(byCreatedAsc);
  }

  return roots;
}

function flattenComments(rootList) {
  const out = [];
  const seen = new Set();

  const pushOnce = (row) => {
    if (seen.has(row.id)) return;
    seen.add(row.id);
    out.push(row);
  };

  for (const m of rootList || []) {
    pushOnce({ ...m, _type: "root" });
    if (Array.isArray(m.replies)) {
      for (const r of m.replies) {
        pushOnce({ ...r, _type: "reply", _parent: m.id });
      }
    }
  }

  return out;
}

/* --------------------------------------------------
   Komponentti
-------------------------------------------------- */
export default function CommentsSection({
  user,
  comments,
  onSend,
  onReplySubmit,
  onLike,
  loading,
  canPost = false,
  perPage = 5,
}) {
  const { width } = useResponsive();
  const isCompactView = width <= VIEW_BREAKPOINTS.announcementsTableMin;

  const [page, setPage] = useState(1);
  const [newText, setNewText] = useState("");
  const [replyDrafts, setReplyDrafts] = useState({});
  const [replyOpen, setReplyOpen] = useState({});

  const roots = useMemo(
    () => buildThreadFromList(normalizeComments(comments)),
    [comments]
  );

  const flatAll = useMemo(() => flattenComments(roots), [roots]);

  useEffect(() => {
    const max = Math.max(1, Math.ceil(flatAll.length / perPage));
    if (page > max) setPage(max);
  }, [flatAll, page, perPage]);

  const totalPages = Math.max(1, Math.ceil(flatAll.length / perPage));

  const pageSlice = useMemo(() => {
    const start = (page - 1) * perPage;
    return flatAll.slice(start, start + perPage);
  }, [flatAll, page, perPage]);

  const toggleReplyFor = (id) =>
    setReplyOpen((p) => ({ ...p, [id]: !p[id] }));

  const setReplyText = (id, v) =>
    setReplyDrafts((p) => ({ ...p, [id]: v }));

  const submitReply = (id) => {
    const body = (replyDrafts[id] || "").trim();
    if (!body) return;
    onReplySubmit?.(id, body);
    setReplyDrafts((p) => ({ ...p, [id]: "" }));
    setReplyOpen((p) => ({ ...p, [id]: false }));
  };

  const currentUserName = `${user?.etunimi || ""} ${user?.sukunimi || ""}`.trim();

  return (
    <>
      <h3 className={styles.sectionTitle}>Keskustelu</h3>

      {canPost && (
        <>
          <textarea
            className={`${styles.input} ${styles.chatInput}`}
            placeholder="Kirjoita kommentti…"
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
          />
          <div className={styles.chatControls}>
            <span className={styles.mutedSmall}></span>
            <button
              className={styles.stateButton}
              onClick={() => {
                const body = newText.trim();
                if (!body) return;
                onSend?.(body);
                setNewText("");
              }}
            >
              Lähetä
            </button>
          </div>
        </>
      )}

      {!loading && pageSlice.length === 0 && (
        <div className={styles.mutedSmall} style={{ marginTop: 8 }}>
          Ei kommentteja vielä.
        </div>
      )}

      <div className={styles.thread}>
        {pageSlice.map((row) => {
          const isRoot = row._type === "root";
          const showReplyBox = isRoot && !!replyOpen[row.id];
          const hasLiked = row.likedBy?.includes(currentUserName);

          return (
            <div
              key={row.id}
              className={styles.msg}
              style={isRoot ? undefined : { marginLeft: 12 }}
            >
              {isCompactView ? (
                <>
                  <div className={styles.commentTopRow}>
                    <div className={styles.commentAuthor}>
                      {row.senderName || "Käyttäjä"}
                    </div>

                    <div className={styles.commentActionsRow}>
                      {row.likedBy?.length > 0 && (
                        <span
                          className={styles.likeWrapper}
                          title={row.likedBy.join("\n")}
                        >
                          👍 {row.likedBy.length}
                        </span>
                      )}

                      <button
                        className={`${styles.linkBtn} ${
                          hasLiked ? styles.liked : ""
                        }`}
                        onClick={() => onLike?.(row.id)}
                      >
                        Tykkää
                      </button>

                      {isRoot && canPost && (
                        <button
                          className={styles.linkBtn}
                          onClick={() => toggleReplyFor(row.id)}
                        >
                          {showReplyBox ? "Peru" : "Vastaa"}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className={styles.commentMetaRow}>
                    {fmtDateTime(row.createdAt)}
                  </div>

                  <div className={styles.msgBody}>{row.body}</div>
                </>
              ) : (
                <>
                  <div className={styles.msgHeader}>
                    <div className={styles.msgSender}>
                      {row.senderName || "Käyttäjä"}
                      <span
                        className={styles.mutedSmall}
                        style={{ marginLeft: 8 }}
                      >
                        {fmtDateTime(row.createdAt)}
                      </span>
                    </div>

                    <div className={styles.actions}>
                      {row.likedBy?.length > 0 && (
                        <span
                          className={styles.likeWrapper}
                          title={row.likedBy.join("\n")}
                        >
                          👍 {row.likedBy.length}
                        </span>
                      )}

                      <button
                        className={`${styles.linkBtn} ${
                          hasLiked ? styles.liked : ""
                        }`}
                        onClick={() => onLike?.(row.id)}
                      >
                        Tykkää
                      </button>

                      {isRoot && canPost && (
                        <button
                          className={styles.linkBtn}
                          onClick={() => toggleReplyFor(row.id)}
                        >
                          {showReplyBox ? "Peru" : "Vastaa"}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className={styles.msgBody}>{row.body}</div>
                </>
              )}

              {showReplyBox && (
                <div className={styles.replyBoxWrap}>
                  <textarea
                    className={`${styles.input} ${styles.chatInput}`}
                    placeholder="Kirjoita vastaus…"
                    value={replyDrafts[row.id] || ""}
                    onChange={(e) => setReplyText(row.id, e.target.value)}
                  />
                  <div className={styles.chatControls}>
                    <button
                      className={styles.stateButton}
                      onClick={() => submitReply(row.id)}
                    >
                      Lähetä
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className={styles.msgPager}>
          <IconButton
            direction="left"
            ariaLabel="Edellinen sivu"
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page <= 1}
          />
          <span className={styles.pageCounterSmall}>
            {page} / {totalPages}
          </span>
          <IconButton
            direction="right"
            ariaLabel="Seuraava sivu"
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
          />
        </div>
      )}
    </>
  );
}